'use client'

import { useRef } from 'react'
import { Stack } from '@christiankaindl/lyts'
import Text from '@shared/components/Text/Text'
import type { LawChange } from '@shared/lib/norms.types'
import type { GazetteLawChangeWithLaw } from '@shared/db/schema/gazettes'
import * as tocStyles from '@/components/Law/Sidebar/Sidebar.css'
import Button from '@shared/components/Button/Button'

function TocEntry({
  title,
  page,
  onClick,
}: {
  title: string
  page?: number
  onClick: () => void
}) {
  return (
    <Button
      variant='flat'
      className={tocStyles.tocItem}
      onClick={onClick}
      style={{
        position: 'relative',
        justifyContent: 'start',
        width: 'calc(100% - 12px)',
      }}
    >
      {page !== undefined && (
        <span className={tocStyles.tocItemEnumeration}>S. {page}</span>
      )}
      <Text
        className={tocStyles.tocItemText}
        maxLines={1}
        style={{ marginLeft: page === undefined ? -6 : undefined }}
      >
        {title}
      </Text>
    </Button>
  )
}

function Toc({ changes }: { changes?: LawChange[] }) {
  return (
    <Stack gap='1px'>
      {changes?.map((change, index) => {
        const page = change.pageNumbers?.[0]
        return (
          <TocEntry
            key={change.normId || index}
            title={change.title}
            page={page}
            onClick={() => {
              const iframe =
                document.querySelector<HTMLIFrameElement>('[data-gazette-pdf]')
              if (!iframe || page === undefined) return
              const baseUrl = iframe.src.split('#')[0].split('&_t=')[0]
              iframe.src = `${baseUrl}&_t=${Date.now()}#page=${page}`
            }}
          />
        )
      })}
      <div style={{ height: '1em' }} />
    </Stack>
  )
}

function PdfViewer({ pdfUrl, title }: { pdfUrl: string; title: string }) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  return (
    <iframe
      ref={iframeRef}
      data-gazette-pdf
      src={pdfUrl}
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        borderRadius: 12,
      }}
      title={title}
    />
  )
}

function TocPG({ changes }: { changes?: GazetteLawChangeWithLaw[] }) {
  return (
    <Stack gap='1px'>
      {changes?.map((change, index) => {
        const page = change.pageNumbers?.[0] ?? undefined
        return (
          <TocEntry
            key={change.lawNormId || index}
            title={change.law?.title ?? change.lawNormId}
            page={page}
            onClick={() => {
              const iframe =
                document.querySelector<HTMLIFrameElement>('[data-gazette-pdf]')
              if (!iframe || page === undefined) return
              const baseUrl = iframe.src.split('#')[0].split('&_t=')[0]
              iframe.src = `${baseUrl}&_t=${Date.now()}#page=${page}`
            }}
          />
        )
      })}
      <div style={{ height: '1em' }} />
    </Stack>
  )
}

export {
  // Also used by `LawPage` for laws that have no structured text, so it is
  // exported un-prefixed too — the viewer itself is document-agnostic.
  PdfViewer,
  PdfViewer as GazettePdfViewer,
  Toc as GazetteToc,
  TocPG as GazetteTocPG,
}
